import { dialogs, errors, i18next, saving } from '@yola/ws-sdk';
import sentry from 'src/js/modules/analytics/sentry';
import errorModule from 'src/js/modules/error';
import store from 'src/js/store';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import segment from 'src/js/modules/analytics/segment';
import auth from 'yola-editor/src/js/modules/auth';
import site from 'yola-editor/src/js/modules/site';
import getNetworkStatus from 'src/js/modules/error/selectors/network-status';
import errorTypes from 'src/js/modules/error/constants/error-types';
import detectDeviceOrientation from 'yola-editor/src/js/utils/detect-device-orientation';
import bowser from 'yola-bowser';
import supportedBrowsers from 'yola-editor/src/js/unsupported-browser-dialog/constants/supported-browsers';
import getNetworkErrorDetails from 'src/js/modules/error/helpers/get-network-error-details';
import showReadOnlyModeDialog from 'src/js/modules/read-only-mode/helpers/show-read-only-mode-dialog';
import constants from '../constants';

const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const { NETWORK_OFFLINE } = constants;

const handleError = (error, type) => {
  const { dispatch, getState } = store;
  const state = getState();

  switch (type) {
    case errors.types.READ_ONLY_ERROR: {
      showReadOnlyModeDialog();
      break;
    }
    case errors.types.SAVING_ERROR: {
      dispatch(
        dialogs.actions.show(dialogTypes.SAVE_ERROR_DIALOG, {
          captions: {
            title: i18next.t('Site saving failed'),
            description: i18next.t('An error occurred while saving your site'),
          },
        })
      );

      dispatch(saving.actions.setStatus(saving.constants.statuses.UNSAVED));

      const errorMessage = error.response ? error.response.data : { detail: error.message };
      trackEvent(events.ERROR_CAUGHT, {
        errorName: 'SITE_SAVE',
        errorMessage: JSON.stringify(errorMessage),
      });
      break;
    }

    default: {
      const networkErrorDetails = errors.helpers.getNetworkErrorDetails(error);

      const editorError = errorModule.helpers.createEditorError({
        errorMessage: error.message,
        networkErrorDetails,
      });
      store.dispatch(errorModule.actions.setCriticalError(editorError));

      const siteId = auth.selectors.getSiteId(state);
      const templateBuildSlug = site.selectors.getSiteTemplateSlug(state);
      const networkStatus = getNetworkStatus(state);
      const isNetworkError = editorError.message.includes(errorTypes.NETWORK_ERROR);

      /*
        In order to be sure that "beforeunload" event fired before
        tracking "ERROR_CAUGHT" event we need to wrap it into macrotask
      */
      setTimeout(() => {
        if (
          (isNetworkError && networkStatus === NETWORK_OFFLINE) ||
          window._ws_resources_unloaded // eslint-disable-line no-underscore-dangle
        ) {
          return;
        }

        const segmentProps = {
          appName: segment.constants.common.APP_NAME,
          errorName: error.name,
          errorMessage: error.message,
          siteId: siteId || null,
          templateBuildSlug,
          deviceOrientation: detectDeviceOrientation(),
          isSupportedBrowser: bowser.isSatisfied(supportedBrowsers),
        };

        if (isNetworkError) {
          segmentProps.networkErrorDetails = getNetworkErrorDetails(error);
        }

        segment.track(segment.constants.events.ERROR_CAUGHT, segmentProps);
      }, 0);
    }
  }
};

const errorCatcher = (error, type, errorInfo) => {
  if (error) {
    console.error(error);

    if (sentry.instance.isInit()) {
      const sentryInstance = sentry.instance.getInstance();

      sentry.instance.captureException(error, errorInfo);

      sentryInstance
        .getCurrentHub()
        .getClient()
        .close(2000)
        .then(() => {
          handleError(error, type, errorInfo);
        });

      return;
    }
  }

  handleError(error, type, errorInfo);
};

export default errorCatcher;
